import { Directive, Renderer2, Input, TemplateRef, ViewContainerRef, ɵstringify as stringify, ChangeDetectorRef, OnDestroy } from '@angular/core';

import { AuthService } from 'src/app/core/services/auth/auth.service';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

const selector = '[hasRole]';
@Directive({
    selector,
    standalone: true
})
export class HasRoleDirective implements OnDestroy {
    private isVisible: boolean;
    private elseAction = 'hide';
    private elseTemplateRef: TemplateRef<any>;
    private rolesInput: string | string[] = [];
    private destroy$ = new Subject<void>();

    constructor(
        private readonly viewContainerRef: ViewContainerRef,
        private readonly templateRef: TemplateRef<any>,
        private readonly renderer: Renderer2,
        private readonly authService: AuthService,
        private readonly cdr: ChangeDetectorRef
    ) {}

    ngOnDestroy() {
        this.destroy$.next();
        this.destroy$.complete();
    }

    @Input()
    set hasRole(roles: string | string[]) {
        this.rolesInput = roles;

        // Une seule souscription "longue" (au lieu d'une par setter)
        this.authService
            .getRoles()
            .pipe(takeUntil(this.destroy$))
            .subscribe(() => {
                const visible = this.authService.hasRole(this.rolesInput);
                if (visible !== this.isVisible) {
                    this.isVisible = visible;
                    this.scheduleUpdateView();
                } else {
                    this.scheduleUpdateView();
                }
            });
    }

    @Input()
    set hasRoleElseDisable(_: void) {
        this.elseAction = 'disabled';
        this.scheduleUpdateView();
    }

    @Input()
    set hasRoleElse(templateRef: TemplateRef<any>) {
        assertTemplate('ngIfElse', templateRef);
        this.elseAction = 'else';
        this.elseTemplateRef = templateRef;
        this.scheduleUpdateView();
    }

    // Décale la mise à jour de la vue pour éviter NG0100
    private scheduleUpdateView() {
        Promise.resolve().then(() => {
            this.updateView();
            this.cdr.markForCheck();
        });
    }

    private updateView() {
        this.viewContainerRef.clear();

        if (this.isVisible) {
            this.viewContainerRef.createEmbeddedView(this.templateRef);
            return;
        }

        if (this.elseAction === 'disabled') {
            const view = this.viewContainerRef.createEmbeddedView(this.templateRef);
            const rootElem = view.rootNodes[0];
            if (rootElem) {
                // Pour les boutons / inputs
                this.renderer.setProperty(rootElem, 'disabled', true);
                this.renderer.addClass(rootElem, 'disabled');
                // Pour les éléments "non form"
                this.renderer.setStyle(rootElem, 'pointer-events', 'none');
            }
            return;
        }

        if (this.elseAction === 'else' && this.elseTemplateRef) {
            this.viewContainerRef.createEmbeddedView(this.elseTemplateRef);
        }
    }
}

export class HasRoleContext {
    public $implicit: any = null;
    public event: any = null;
}

function assertTemplate(property: string, templateRef: TemplateRef<any> | null): void {
    const isTemplateRefOrNull = !!(!templateRef || templateRef.createEmbeddedView);
    if (!isTemplateRefOrNull) {
        throw new Error(`${property} must be a TemplateRef, but received '${stringify(templateRef)}'.`);
    }
}
